/*
  The API client (BUILD-PLAN 2.11.3).

  - `get<T>(path, params)` reads.
  - `command<T>(method, path, body)` changes something: it adds `command_id` (a UUID made here) and the
    `X-CSRF-Token` header, and turns a refusal into a typed `RefusalError`.
  - `upload<T>(path, formData)` sends a file the same way (multipart; `command_id` is a form field).

  A command that succeeds returns `{ok, events, data}` with the view named in the plan inside `data`.
*/

import type { CommandResult, Refusal, RefusalBody, RefusalCode } from "./types";

export type Primitive = string | number | boolean;
export type Params = Record<string, Primitive | readonly Primitive[] | null | undefined>;

/** What a command returns when it succeeds: the `CommandResult` fields with the view typed. */
export interface CommandReply<T> {
  ok: true;
  events: number[];
  data: T;
}

/** A refusal from the server, with the plain message the person reads. */
export class RefusalError extends Error {
  readonly code: RefusalCode;
  readonly details: unknown;
  readonly status: number;
  readonly refusal: Refusal;

  constructor(refusal: Refusal, status: number) {
    super(refusal.message);
    this.name = "RefusalError";
    this.refusal = refusal;
    this.code = refusal.code;
    this.details = refusal.details;
    this.status = status;
  }
}

/** The server did not answer at all (no connection, or the connection broke). */
export class NetworkError extends Error {
  constructor() {
    super("The server did not answer. Check your connection and try again.");
    this.name = "NetworkError";
  }
}

let csrfToken: string | null = null;

/** The token from `/api/me`; sent as `X-CSRF-Token` with every changing request. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export type ApiSpace = "workspace" | "practice";

let apiSpace: ApiSpace = "workspace";

/**
 * The space the person works in, from `/api/me` (`active.space`). In the practice space the endpoints of the
 * discipline are reached under `/api/practice/...` instead of `/api/ws/...` (BUILD-PLAN 2.9, BR-3).
 */
export function setApiSpace(space: ApiSpace): void {
  apiSpace = space;
}

export function getApiSpace(): ApiSpace {
  return apiSpace;
}

export function newCommandId(): string {
  return crypto.randomUUID();
}

/** Build `path?query`. Missing and null values are left out; a list becomes repeated keys. */
export function buildUrl(path: string, params?: Params): string {
  if (!params) return path;
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) query.append(key, String(item));
    } else {
      query.append(key, String(value));
    }
  }
  const text = query.toString();
  return text ? `${path}${path.includes("?") ? "&" : "?"}${text}` : path;
}

function isRefusalBody(body: unknown): body is RefusalBody {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { ok?: unknown }).ok === false &&
    typeof (body as { refusal?: { code?: unknown } }).refusal?.code === "string"
  );
}

function plainRefusal(response: Response): Refusal {
  // A proxy or a restart can answer without the plan's body: the two answers that mean something are kept.
  if (response.status === 401) return { code: "not_signed_in", message: "Please sign in.", details: { status: 401 } };
  if (response.status === 403) return { code: "forbidden", message: "Your role does not allow this.", details: { status: 403 } };
  // A plain sentence, with the number only as a reference for the admin (no "Bad Gateway" and no other technical words).
  const message = response.status
    ? `Something went wrong on the server (${response.status}). Please try again, and tell the admin if it stays.`
    : "The server did not answer. Check your connection and try again.";
  return { code: "conflict", message, details: { status: response.status } };
}

/** `fetch`, with a broken connection turned into a plain `NetworkError`. */
async function send(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch {
    throw new NetworkError();
  }
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

async function unwrap(response: Response): Promise<unknown> {
  const body = await readBody(response);
  if (isRefusalBody(body)) throw new RefusalError(body.refusal, response.status);
  if (!response.ok) throw new RefusalError(plainRefusal(response), response.status);
  return body;
}

function isCommandResult(body: unknown): body is CommandResult {
  return typeof body === "object" && body !== null && typeof (body as CommandResult).ok === "boolean" && Array.isArray((body as CommandResult).events);
}

function toReply<T>(body: unknown): CommandReply<T> {
  if (isCommandResult(body)) {
    if (!body.ok) {
      const refusal = body.refusal ?? { code: "conflict" as const, message: "The command did not go through.", details: null };
      throw new RefusalError(refusal, 200);
    }
    return { ok: true, events: body.events, data: body.data as T };
  }
  // Sign-in and a few others answer with the view itself.
  return { ok: true, events: [], data: body as T };
}

/** Read a view. */
export async function get<T>(path: string, params?: Params, init?: RequestInit): Promise<T> {
  const response = await send(buildUrl(path, params), {
    ...init,
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(init?.headers ?? {}) },
  });
  return (await unwrap(response)) as T;
}

/** Read a picture or another file as a blob. */
export async function getBlob(path: string, params?: Params): Promise<Blob> {
  const response = await send(buildUrl(path, params), { method: "GET", credentials: "same-origin" });
  if (!response.ok) {
    const body = await readBody(response);
    throw new RefusalError(isRefusalBody(body) ? body.refusal : plainRefusal(response), response.status);
  }
  return response.blob();
}

type Method = "POST" | "PUT" | "PATCH" | "DELETE";

export interface CommandOptions {
  /**
   * Add a `command_id` to the body (the default). Sign-in is the one request whose contract has none:
   * it is not a repeatable decision, and the server refuses fields it does not know.
   */
  commandId?: boolean;
}

/** Change something. `command_id` is added unless the body has one. */
export async function command<T>(method: Method, path: string, body: Record<string, unknown> = {}, options: CommandOptions = {}): Promise<CommandReply<T>> {
  const headers: Record<string, string> = { Accept: "application/json", "Content-Type": "application/json" };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const withId = options.commandId !== false;
  const response = await send(path, {
    method,
    credentials: "same-origin",
    headers,
    body: JSON.stringify(withId ? { ...body, command_id: body.command_id ?? newCommandId() } : body),
  });
  return toReply<T>(await unwrap(response));
}

/** Send a file. `command_id` is added as a form field unless the form has one. */
export async function upload<T>(path: string, form: FormData): Promise<CommandReply<T>> {
  if (!form.has("command_id")) form.set("command_id", newCommandId());
  const headers: Record<string, string> = { Accept: "application/json" };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const response = await send(path, { method: "POST", credentials: "same-origin", headers, body: form });
  return toReply<T>(await unwrap(response));
}
